<script setup>
// ---------------------------------------------------------------------------
// The money that reached the Office, split by sector.
//
// The whole ring is everything the Office reviewed — not the market. What share
// of the market that is, is the question the ring below this one answers.
//
// This is the one shape a pie is actually good at: parts of a single whole, few
// enough of them to tell apart. The palette was built for exactly six plus a
// residual — see check_palette.
//
// Ticking sectors in the filter narrows the case data, so the ring is redrawn
// from what is left rather than dimmed: with one sector chosen the whole ring is
// that sector, which is what "sto procent" has to mean here.
// ---------------------------------------------------------------------------

import { computed } from 'vue'

import { money, sectorVolume } from '../cases.js'
import { useContracts } from '../useCases.js'

// Segments are drawn by dashing the circumference and pushing each one along by
// what came before it: no arc maths, no shape library.
const R = 60
const CIRCUMFERENCE = 2 * Math.PI * R

const props = defineProps({
    src: { type: String, required: true },
    keep: { type: String, default: '6' },
    restLabel: { type: String, default: '' },
    // Pipe-separated: billions, millions, thousands.
    units: { type: String, default: '' },
    totalLabel: { type: String, default: '' },
    emptyText: { type: String, default: '' },
    errorText: { type: String, default: '' }
})

const { rows, visible, failed } = useContracts(props.src)

// An empty total before the file lands is not the filter narrowing it away.
const loaded = computed(() => Boolean(rows.value.length))

const data = computed(() => sectorVolume(visible.value, Number(props.keep)))

const total = computed(() => data.value.total)

const segments = computed(() => {
    const parts = data.value.items.map((item, index) => ({
        key: item.label,
        label: item.label,
        volume: item.value,
        lit: true,
        tone: index + 1
    }))

    if (data.value.rest > 0) {
        parts.push({
            key: 'rest',
            label: `${props.restLabel} (${data.value.restCount})`,
            volume: data.value.rest,
            lit: true,
            tone: 'rest'
        })
    }

    let offset = 0

    return parts.map((part) => {
        const length = total.value ? (part.volume / total.value) * CIRCUMFERENCE : 0
        const segment = { ...part, dash: `${length} ${CIRCUMFERENCE - length}`, offset: -offset }

        offset += length
        return segment
    })
})

const billions = (value) => money(value, props.units.split('|'))

const percent = (value) =>
    (value / (total.value || 1)).toLocaleString('cs-CZ', {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    })
</script>

<template>
    <p v-if="failed" class="chart__failed">{{ errorText }}</p>

    <p v-else-if="loaded && !total" class="chart__failed">{{ emptyText }}</p>

    <div v-else-if="total" class="statbox statbox--panel donut">
        <svg class="donut__plot" viewBox="0 0 160 160" role="img" :aria-label="totalLabel">
            <g transform="translate(80 80) rotate(-90)">
                <circle
                    v-for="segment in segments"
                    :key="segment.key"
                    class="donut__seg"
                    :class="[`donut__seg--${segment.tone}`, { 'is-dim': !segment.lit }]"
                    :r="R"
                    :stroke-dasharray="segment.dash"
                    :stroke-dashoffset="segment.offset"
                />
            </g>
        </svg>

        <div class="donut__key">
            <p class="donut__total">
                <span class="donut__amount">{{ billions(total) }}</span>
                <span class="donut__caption">{{ totalLabel }}</span>
            </p>

            <ul class="donut__list">
                <li
                    v-for="segment in segments"
                    :key="segment.key"
                    :class="{ 'is-dim': !segment.lit }"
                >
                    <span class="donut__swatch" :class="`donut__seg--${segment.tone}`"></span>
                    <span class="donut__name">{{ segment.label }}</span>
                    <span class="donut__amount-small">{{ billions(segment.volume) }}</span>
                    <span class="donut__share">{{ percent(segment.volume) }}</span>
                </li>
            </ul>
        </div>
    </div>
</template>
